/**
 * PAGE: LISTE ASSOCIATIONS (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  ColumnSelectorModal,
  CreateAssociationModal,
  FiltersModal,
  ImportCSVModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { AssociationSidePanel } from '../components/panels/AssociationSidePanel';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import { BulkActionsBar, ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useAssociations } from '../hooks/useAssociations';
import { useSupabase } from '../hooks/useSupabase';
import { useOpenFromSearch } from '../hooks/useOpenFromSearch';
import { usePermissions } from '../hooks/usePermissions';
import type { UpdateAssociationInput } from '../schemas/association';
import type { Association } from '../types/association';
import { ASSOCIATION_CONFIG } from '../config/entities/association.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
} from '../lib/entity-config';

export function AssociationsListPage() {
  const supabase = useSupabase();
  const {
    data: associations,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshAssociations,
  } = useAssociations();

  // Permissions utilisateur
  const { canCreateEntity, canDeleteEntity, canImportData, canExportData, canDeleteBulk } =
    usePermissions();

  // Side panel state (logique spécifique)
  const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedAssociation(null);
  }, []);

  // Générer automatiquement depuis ASSOCIATION_CONFIG
  const filterConfigs = useMemo(() => generateFilterConfigs(ASSOCIATION_CONFIG), []);
  const csvColumns = useMemo(() => generateCSVColumns(ASSOCIATION_CONFIG), []);

  // Générer les transformers de base + ajouter le custom pour crews_count
  const csvTransformers = useMemo(() => {
    const baseTransformers = generateCSVTransformers(ASSOCIATION_CONFIG);
    return {
      ...baseTransformers,
      crews: (value: any) => {
        const count = value?.[0]?.count ?? 0;
        return String(count);
      },
    };
  }, []);

  const importConfig = useMemo(() => generateImportConfig(ASSOCIATION_CONFIG), []);

  const entity = useEntityList({
    data: associations,
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: refreshAssociations,
    entityName: ASSOCIATION_CONFIG.entityName,
    entityNamePlural: ASSOCIATION_CONFIG.entityNamePlural,
    searchableFields: ['name'] as (keyof Association)[],
    filterConfigs,
    csvFileName: 'associations.csv',
    csvColumns,
    csvTransformers,
    importConfig,
    onDeleteSuccess: handleCloseSidePanel,
  });

  const { handleDeleteClick } = entity;

  const handleOpenSidePanel = useCallback((association: Association) => {
    setSelectedAssociation(association);
    setIsSidePanelOpen(true);
  }, []);

  // Ouvrir automatiquement le side panel depuis la recherche globale
  useOpenFromSearch({
    data: associations,
    getId: (a) => a.id,
    onOpen: handleOpenSidePanel,
    loading,
  });

  const handleUpdateAssociation = useCallback(
    async (id: string, data: UpdateAssociationInput) => {
      if (!update) return;

      try {
        await update(id, data);
        // Rafraîchir la liste pour récupérer les relations mises à jour
        await refreshAssociations();
        // Mettre à jour le selectedAssociation avec les nouvelles données
        if (selectedAssociation?.id === id) {
          const updatedAssociation = associations.find((a) => a.id === id);
          if (updatedAssociation) {
            setSelectedAssociation(updatedAssociation);
          }
        }
        toast.success('Association mise à jour avec succès');
      } catch (error) {
        console.error('Error updating association:', error);
        toast.error('Erreur lors de la mise à jour');
      }
    },
    [update, selectedAssociation, refreshAssociations, associations]
  );

  const handleDeleteFromPanel = useCallback(
    async (id: string) => {
      if (selectedAssociation) {
        entity.handleDeleteClick(id, selectedAssociation.name);
      }
    },
    [selectedAssociation, entity]
  );

  // Vérifier si des actions sont possibles
  const hasAnyBulkAction = canDeleteBulk('association') || canExportData('association');
  const hasAnyRowAction = canDeleteEntity('association');

  const columns: ColumnDef<Association>[] = useMemo(
    () => [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<Association>[])
        : []),
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        size: 300,
        enableSorting: true,
      },
      {
        id: 'crews_count',
        header: 'Équipages',
        cell: ({ row }) => {
          const count = row.original.crews?.[0]?.count ?? 0;
          return <TableCell>{count}</TableCell>;
        },
        size: 100,
        enableSorting: false,
      },
      // Colonne actions uniquement si des actions sont possibles
      ...(hasAnyRowAction
        ? ([
            {
              id: 'actions',
              header: 'Action',
              cell: ({ row }: any) => (
                <ActionsCell
                  onView={() => handleOpenSidePanel(row.original)}
                  onDelete={() => handleDeleteClick(row.original.id, row.original.name)}
                  canDelete={canDeleteEntity('association')}
                />
              ),
              size: 120,
            },
          ] as ColumnDef<Association>[])
        : []),
    ],
    [handleOpenSidePanel, handleDeleteClick, canDeleteEntity, hasAnyBulkAction, hasAnyRowAction]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(ASSOCIATION_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Associations' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des associations..." />
      </PageLayout>
    );
  }

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Associations' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des associations"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <>
          <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Associations' }]} />
          <div className="w-full h-px bg-zinc-200" />
        </>
      }
      sidePanel={
        selectedAssociation && (
          <AssociationSidePanel
            isOpen={isSidePanelOpen}
            onClose={handleCloseSidePanel}
            association={selectedAssociation}
            onSubmit={handleUpdateAssociation}
            onDelete={handleDeleteFromPanel}
          />
        )
      }
    >
      <PageContent
        title="Associations"
        description="Gérez les associations partenaires"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('association') ? entity.handleExportCSV : undefined}
            onUpload={
              canImportData('association') ? () => entity.setIsImportModalOpen(true) : undefined
            }
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('association') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateAssociationModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${ASSOCIATION_CONFIG.entityNamePlural}`
            : `Supprimer l'${ASSOCIATION_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${ASSOCIATION_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: association, error: supabaseError } = await supabase
            .from('associations')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return association;
        }}
        onUpdate={update}
        existingData={associations}
        entityName={ASSOCIATION_CONFIG.entityName}
        entityNamePlural={ASSOCIATION_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={ASSOCIATION_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('association')}
          canExport={canExportData('association')}
        />
      )}
    </PageLayout>
  );
}
